<div
  class="rounded-3xl border border-gray-6/60 bg-white/70 dark:bg-gray-1/70 backdrop-blur-xl overflow-hidden transition-all duration-200 shadow-sm"
  [class.shadow-xl]="isScrolled()"
  [class.shadow-black/5]="isScrolled()"
  [class.border-gray-7/70]="isScrolled()"
  [class.bg-white/85]="isScrolled()"
  [class.dark:bg-gray-1/85]="isScrolled()"
>
  <!-- Top Cockpit: Title, Quick Meta & Status Badges -->
  <div
    class="flex flex-col md:flex-row md:items-center justify-between gap-3 sm:gap-4 transition-all duration-200"
    [class.p-3]="isScrolled()"
    [class.sm:px-5]="isScrolled()"
    [class.sm:py-2.5]="isScrolled()"
    [class.p-5]="!isScrolled()"
    [class.sm:p-6]="!isScrolled()"
  >
    <!-- Left: Property Hero Identity -->
    <div class="flex items-center gap-3.5 min-w-0 flex-1">
      <div class="min-w-0">
        <div class="flex items-center gap-2 flex-wrap">
          <h1
            class="font-black text-gray-12 tracking-tight m-0 truncate transition-all duration-200"
            [class.text-sm]="isScrolled()"
            [class.sm:text-base]="isScrolled()"
            [class.text-xl]="!isScrolled()"
            [class.sm:text-2xl]="!isScrolled()"
          >
            {{ propertyTitle() }}
          </h1>
        </div>

        <div
          class="flex items-center gap-2 text-xs text-gray-11 flex-wrap font-medium transition-all duration-200"
          [class.mt-0]="isScrolled()"
          [class.mt-1]="!isScrolled()"
        >
          @if (offerTypeLabel() || propertyTypeLabel() || residenceTypeLabel()) {
          <div class="flex items-center gap-1.5 shrink-0 flex-wrap">
            @if (offerTypeLabel()) {
            <span
              class="inline-flex items-center px-2.5 py-0.5 rounded-md bg-indigo-a3 text-indigo-11 border border-indigo-a4 text-xs font-bold"
            >
              {{ offerTypeLabel() }}
            </span>
            } @if (propertyTypeLabel()) {
            <span
              class="inline-flex items-center px-2.5 py-0.5 rounded-md bg-gray-a3 text-gray-12 border border-gray-a5 text-xs font-bold"
            >
              {{ propertyTypeLabel() }}
            </span>
            }
          </div>
          } @if (locationSummary()) {
          <span class="flex items-center gap-1 text-gray-12"> {{ locationSummary() }} </span>
          } @if (priceFormatted()) {
          <span class="text-gray-6">•</span>
          <span class="font-bold text-gray-12 flex items-baseline gap-1">
            {{ priceFormatted() }} @if (pricePerSquareMeterFormatted(); as priceM2) {
            <span class="font-medium text-[11px] text-gray-11">({{ priceM2 }}/m²)</span>
            }
          </span>
          } @if (surfaceFormatted() && !isScrolled()) {
          <span class="text-gray-6">•</span>
          <span>{{ surfaceFormatted() }}</span>
          }
          <span class="text-gray-6">•</span>
          @if (property().definition.reference) {
          <span
            class="font-mono text-[10px] text-gray-10 bg-gray-2 px-1.5 py-0.5 rounded border border-gray-5"
          >
            REF {{ property().definition.reference }}
          </span>
          }
        </div>
      </div>
    </div>

    <!-- Right: Badges & Completion Trigger -->
    <div class="flex items-center gap-2 flex-wrap shrink-0 self-start md:self-auto">
      <!-- Missing Info / Completion Pill Button -->
      @if (missingItems().length === 0) {
      <span
        class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-jade-a3 text-jade-11 border border-jade-a6 font-bold text-xs shadow-2xs tracking-wide"
      >
        <mat-icon class="text-jade-9 text-sm w-3.5 h-3.5">verified</mat-icon>
        <span class="hidden lg:inline">100% Complet ✨</span>
      </span>
      } @else {
      <button
        type="button"
        (click)="toggleDrawer()"
        class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-a3 text-amber-11 border border-amber-a6 font-bold text-xs hover:bg-amber-a4 transition-all cursor-pointer shadow-2xs"
        [class.ring-2]="drawerExpanded()"
        [class.ring-amber-8]="drawerExpanded()"
      >
        <span class="w-2 h-2 rounded-full bg-amber-9 animate-pulse"></span>
        <span class="hidden lg:inline">{{ completionPercentage() }}% complété</span>
        <span
          class="px-1.5 py-0.5 rounded-full bg-amber-a5 text-amber-12 text-[10px] font-black tracking-tight"
        >
          {{ missingItems().length }} manquant{{ missingItems().length > 1 ? 's' : '' }}
        </span>
        <mat-icon
          class="text-xs w-3.5 h-3.5 transition-transform duration-200"
          [class.rotate-180]="drawerExpanded()"
        >
          expand_more
        </mat-icon>
      </button>
      }

      <!-- Verification Badge -->
      @if (property().state.status !== EntityStatus.ACTIVE) { @switch (verificationStatus()) { @case
      (PropertyVerificationStatus.VERIFIED) {
      <span
        class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-jade-a3 text-jade-11 border border-jade-a6 font-bold text-[11px] uppercase tracking-wider"
      >
        <mat-icon class="text-jade-9 text-sm w-3.5 h-3.5" svgIcon="check" />
        Certifié
      </span>
      } @case (PropertyVerificationStatus.REJECTED) {
      <span
        class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-ruby-a3 text-ruby-11 border border-ruby-a6 font-bold text-[11px] uppercase tracking-wider"
      >
        <mat-icon class="text-ruby-9 text-sm w-3.5 h-3.5" svgIcon="circle-alert" />
        Justificatif rejeté
      </span>
      } @case (PropertyVerificationStatus.FLAGGED) {
      <span
        class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-a3 text-amber-11 border border-amber-a6 font-bold text-[11px] uppercase tracking-wider"
      >
        <mat-icon class="text-amber-9 text-sm w-3.5 h-3.5" svgIcon="triangle-alert" />
        Signalé
      </span>
      } @default { @if (isOwnershipPendingReview()) {
      <span
        class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-a3 text-blue-11 border border-blue-a6 font-bold text-[11px] uppercase tracking-wider"
      >
        <mat-icon class="text-blue-9 text-sm w-3.5 h-3.5" svgIcon="clock" />
        En attente de validation
      </span>
      } } } }

      <!-- Publication State Badge -->
      @if (verificationStatus() === PropertyVerificationStatus.VERIFIED) { @if (showPublicationStatus()) { @if
      (isReadyToPublish()) {
      <span
        class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-a3 text-indigo-11 border border-indigo-a6 font-bold text-[11px] uppercase tracking-wider shadow-2xs"
      >
        <span class="w-1.5 h-1.5 rounded-full bg-indigo-9 animate-pulse"></span>
        Prête à publier
      </span>
      } @else { @switch (property().state.status) { @case (EntityStatus.ACTIVE) {
      <span
        class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-jade-a3 text-jade-11 border border-jade-a6 font-bold text-[11px] uppercase tracking-wider"
      >
        <span class="w-1.5 h-1.5 rounded-full bg-jade-9"></span>
        Publiée
      </span>
      } @case (EntityStatus.DEACTIVATED) {
      <span
        class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-a3 text-gray-11 border border-gray-a6 font-bold text-[11px] uppercase tracking-wider"
      >
        <span class="w-1.5 h-1.5 rounded-full bg-gray-9"></span>
        Désactivé
      </span>
      } @default {
      <span
        class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-a3 text-orange-11 border border-orange-a6 font-bold text-[11px] uppercase tracking-wider"
      >
        <span class="w-1.5 h-1.5 rounded-full bg-orange-9"></span>
        Brouillon
      </span>
      } } } } }

      <!-- Dedicated subtle button for "Voir la fiche" on screens where left sidebar is hidden (< 2xl) -->
      @if (editable()) {

      <mat-menu #viewSheetMenu="matMenu" class="shadow-xl">
        <a mat-menu-item [routerLink]="['/properties', property()._id]">
          <mat-icon class="text-indigo-9!">visibility</mat-icon>
          <div class="flex flex-col text-left">
            <span class="font-medium text-xs">Fiche complète du bien</span>
            <span class="text-[10px] text-gray-10 leading-tight"
              >Toutes vos informations, documents et diagnostics</span
            >
          </div>
        </a>
        <a mat-menu-item [routerLink]="['/properties', property()._id, 'preview']">
          <mat-icon class="text-indigo-9!">badge</mat-icon>
          <div class="flex flex-col text-left">
            <span class="font-medium text-xs">Aperçu agent (sans mandat)</span>
            <span class="text-[10px] text-gray-10 leading-tight"
              >Ce que voient les agents immobiliers avant de postuler</span
            >
          </div>
        </a>
      </mat-menu>
      }

      <!-- Ellipsis Menu -->
      <button
        type="button"
        [matMenuTriggerFor]="propertyMenu"
        class="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-a3 hover:bg-gray-a4 text-gray-11 hover:text-gray-12 border border-gray-a6 transition-colors cursor-pointer shrink-0"
        aria-label="Options de l'annonce"
      >
        <mat-icon class="!w-4 !h-4 !text-[18px] flex items-center justify-center leading-none"
          >more_vert</mat-icon
        >
      </button>

      <mat-menu #propertyMenu="matMenu" class="shadow-xl">
        @if (editable()) {
        <a mat-menu-item [routerLink]="['/properties', property()._id]">
          <mat-icon class="text-indigo-9!">visibility</mat-icon>
          <span>Fiche complète du bien</span>
        </a>
        <a mat-menu-item [routerLink]="['/properties', property()._id, 'preview']">
          <mat-icon class="text-indigo-9!">badge</mat-icon>
          <span>Aperçu agent (sans mandat)</span>
        </a>
        }
        <button mat-menu-item (click)="onManageSettings()">
          <mat-icon class="text-indigo-9!">settings</mat-icon>
          <span>Paramètres de l'annonce</span>
        </button>
      </mat-menu>
    </div>
  </div>

  <!-- Integrated Reusable Section Navigation Ribbon (Visible on xl up to 2xl only) -->
  <div class="hidden lg:block 2xl:hidden border-t border-gray-5/70 bg-gray-1/40 px-3 py-1 sm:px-4">
    <etxe-property-section-nav [sections]="sections()" variant="horizontal" />
  </div>

  <!-- Expandable Missing Items & Completion Drawer -->
  <etxe-property-missing-info
    [property]="property()"
    variant="drawer"
    mode="edit"
    [(expanded)]="drawerExpanded"
    (actionClicked)="onActionClicked($event)"
  />

  <!-- Integrated Verification Warning Callout -->
  @if (!isVerified()) {
  <div
    class="border-t px-4 py-3 sm:px-5 sm:py-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-colors"
    [class.bg-blue-1/80]="isOwnershipPendingReview()"
    [class.border-blue-5]="isOwnershipPendingReview()"
    [class.text-blue-12]="isOwnershipPendingReview()"
    [class.bg-amber-1/80]="!isOwnershipPendingReview() && (verificationStatus() === PropertyVerificationStatus.UNVERIFIED || verificationStatus() === PropertyVerificationStatus.FLAGGED)"
    [class.border-amber-5]="!isOwnershipPendingReview() && (verificationStatus() === PropertyVerificationStatus.UNVERIFIED || verificationStatus() === PropertyVerificationStatus.FLAGGED)"
    [class.text-amber-12]="!isOwnershipPendingReview() && (verificationStatus() === PropertyVerificationStatus.UNVERIFIED || verificationStatus() === PropertyVerificationStatus.FLAGGED)"
    [class.bg-ruby-1/80]="verificationStatus() === PropertyVerificationStatus.REJECTED"
    [class.border-ruby-5]="verificationStatus() === PropertyVerificationStatus.REJECTED"
    [class.text-ruby-12]="verificationStatus() === PropertyVerificationStatus.REJECTED"
  >
    <div class="flex items-center gap-3">
      <div
        class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
        [class.bg-blue-3]="isOwnershipPendingReview()"
        [class.text-blue-11]="isOwnershipPendingReview()"
        [class.bg-amber-3]="!isOwnershipPendingReview() && (verificationStatus() === PropertyVerificationStatus.UNVERIFIED || verificationStatus() === PropertyVerificationStatus.FLAGGED)"
        [class.text-amber-11]="!isOwnershipPendingReview() && (verificationStatus() === PropertyVerificationStatus.UNVERIFIED || verificationStatus() === PropertyVerificationStatus.FLAGGED)"
        [class.bg-ruby-3]="verificationStatus() === PropertyVerificationStatus.REJECTED"
        [class.text-ruby-11]="verificationStatus() === PropertyVerificationStatus.REJECTED"
      >
        @if (isOwnershipPendingReview()) {
        <mat-icon class="text-base w-4 h-4" svgIcon="clock" />
        } @else if (verificationStatus() === PropertyVerificationStatus.REJECTED) {
        <mat-icon class="text-base w-4 h-4" svgIcon="circle-alert" />
        } @else {
        <mat-icon class="text-base w-4 h-4" svgIcon="triangle-alert" />
        }
      </div>

      <div class="text-xs leading-tight">
        @if (isOwnershipPendingReview()) {
        <span class="font-semibold">Vérification en cours :</span>
        <span class="opacity-90 ml-1">
          Votre justificatif de propriété a bien été déposé et est en cours d'examen par notre équipe.
        </span>
        } @else if (verificationStatus() === PropertyVerificationStatus.REJECTED) {
        <span class="font-semibold">Action requise :</span>
        <span class="opacity-90 ml-1">
          Votre justificatif a été rejeté. Veuillez fournir un document lisible et conforme pour pouvoir
          publier votre annonce.
        </span>
        } @else if (verificationStatus() === PropertyVerificationStatus.FLAGGED) {
        <span class="font-semibold">Annonce signalée :</span>
        <span class="opacity-90 ml-1">
          Une vérification manuelle de votre justificatif est en cours par l'équipe ETXE.
        </span>
        } @else {
        <span class="font-semibold">Action requise pour diffuser :</span>
        <span class="opacity-90 ml-1">
          Déposez votre <strong>justificatif de propriété</strong> (avis de taxe foncière ou attestation) pour
          rendre votre bien public.
        </span>
        }
      </div>
    </div>

    <div class="shrink-0 self-end sm:self-auto">
      <button
        mat-stroked-button
        type="button"
        (click)="scrollToDocuments()"
        [class.border-blue-7!]="isOwnershipPendingReview()"
        [class.text-blue-11!]="isOwnershipPendingReview()"
        [class.hover:bg-blue-3!]="isOwnershipPendingReview()"
        [class.border-amber-7!]="!isOwnershipPendingReview() && (verificationStatus() === PropertyVerificationStatus.UNVERIFIED || verificationStatus() === PropertyVerificationStatus.FLAGGED)"
        [class.text-amber-11!]="!isOwnershipPendingReview() && (verificationStatus() === PropertyVerificationStatus.UNVERIFIED || verificationStatus() === PropertyVerificationStatus.FLAGGED)"
        [class.hover:bg-amber-3!]="!isOwnershipPendingReview() && (verificationStatus() === PropertyVerificationStatus.UNVERIFIED || verificationStatus() === PropertyVerificationStatus.FLAGGED)"
        [class.border-ruby-7!]="verificationStatus() === PropertyVerificationStatus.REJECTED"
        [class.text-ruby-11!]="verificationStatus() === PropertyVerificationStatus.REJECTED"
        [class.hover:bg-ruby-3!]="verificationStatus() === PropertyVerificationStatus.REJECTED"
        class="text-xs! font-semibold! py-1! px-3! h-auto! cursor-pointer"
      >
        <mat-icon
          class="text-sm! w-3.5! h-3.5! -ml-1 mr-1"
          [svgIcon]="isOwnershipPendingReview() ? 'clock' : 'shield-check'"
        />
        <span>
          {{ isOwnershipPendingReview() || verificationStatus() !== PropertyVerificationStatus.UNVERIFIED ?
          'Voir le document' : 'Ajouter mon justificatif' }}
        </span>
      </button>
    </div>
  </div>
  } @else if (isReadyToPublish()) {
  <!-- Ready to publish (Certified & Pending 1st Owner activation) Banner -->
  <div
    class="border-t border-indigo-5 bg-indigo-1/80 text-indigo-12 px-4 py-3 sm:px-5 sm:py-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-colors"
  >
    <div class="flex items-center gap-3">
      <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-3 text-indigo-11">
        <mat-icon class="text-base w-4 h-4" svgIcon="sparkles" />
      </div>
      <div class="text-xs leading-tight">
        <span class="font-bold text-indigo-12">Félicitations, votre bien est certifié !</span>
        <span class="opacity-90 ml-1">
          Votre justificatif a été validé avec succès. Votre annonce est prête à être publiée pour recevoir
          les premières candidatures d'agents.
        </span>
      </div>
    </div>

    @if (editable()) {
    <div class="shrink-0 self-end sm:self-auto">
      <button
        mat-flat-button
        type="button"
        (click)="onPublishClick()"
        [disabled]="isPublishing()"
        class="primary text-xs font-semibold py-1 px-3 h-auto shadow-sm flex items-center gap-1.5"
      >
        @if (isPublishing()) {
        <mat-spinner diameter="14" class="inline-block" />
        } @else {
        <mat-icon class="text-sm! w-3.5! h-3.5! -ml-0.5 mr-1" svgIcon="rocket" />
        }
        <span>{{ isPublishing() ? 'Publication en cours...' : 'Publier mon annonce' }}</span>
      </button>
    </div>
    }
  </div>
  }
</div>
